import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { RouterProvider } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import { ThemeProvider } from '@/shared/ui/theme/ThemeProvider';
import { AuthProvider } from '@/modules/auth/presentation/context/AuthContext';
import { ProfileProvider } from '@/modules/profiles/presentation/context/ProfileContext';
import { DeviceEnrollProvider } from '@/modules/devices/presentation/context/DeviceEnrollContext';
import { DeviceEnrollBanner } from '@/shared/ui/banner/DeviceEnrollBanner';
import { DeviceLicenseProvider } from '@/modules/devices/presentation/context/DeviceLicenseContext';
import { DeviceLicenseModal } from '@/modules/devices/presentation/components/DeviceLicenseModal';
import { ToastProvider } from '@/shared/ui/toast/ToastContext';
import { OfflineExitGuard } from '@/shared/hooks/OfflineExitGuard';
import { ConsoleGate } from '@/shared/devtools/ConsoleGate';
import { queryClient } from '@/shared/queryClient';
import { router } from '@/router';
import { i18n } from '@/shared/i18n';
import { initSyncWorker } from '@/shared/sync/syncWorker.singleton';
import { initServiceWorkerBridge } from '@/shared/sync/sw-bridge';
import { eagerInitializeOnnxModels } from '@/modules/attendance/infrastructure/face/eagerModelInit';
import { debugTelemetryService } from '@/shared/services/debugTelemetryService';
import { ErrorBoundary } from "@sentry/react";
import { ErrorSentryPage } from './shared/ui/error/error.sentry';
import '@/index.css';
import "./shared/utils/instrument";

// Register Service Worker for PWA (only in production)
// vite-plugin-pwa generates this file during build
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch((error) => {
      console.error('SW registration failed:', error);
    });
  });
}

/** Hides the #app-loader element with a fade-out transition. */
function hideLoader(): void {
  const loader = document.getElementById('app-loader');
  if (!loader) return;
  loader.style.transition = 'opacity 0.5s ease-out';
  loader.style.opacity = '0';
  setTimeout(() => loader.remove(), 500);
}

/** Updates the loader's label to reflect which startup stage is still pending. */
function setLoaderMessage(text: string): void {
  const label = document.getElementById('al-label');
  if (label) label.textContent = text;
}

/**
 * Returns a Promise that resolves when the Service Worker has finished
 * pre-caching all assets (SW reaches 'activated' state).
 * Resolves immediately when SW is already active or unavailable.
 */
function swReadyPromise(): Promise<void> {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) {
    return Promise.resolve();
  }
  return navigator.serviceWorker.ready
    .then((registration) => {
      const sw = registration.active ?? registration.waiting ?? registration.installing;
      if (!sw || sw.state === 'activated') return;
      return new Promise<void>((resolve) => {
        sw.addEventListener('statechange', function onStateChange() {
          if (sw.state === 'activated') {
            sw.removeEventListener('statechange', onStateChange);
            resolve();
          }
        });
      });
    })
    .catch(() => { /* ignore SW errors — app still usable */ });
}

/**
 * Hides the #app-loader only when BOTH conditions are true:
 *   1. Service Worker has finished pre-caching all assets (~126 MB)
 *   2. ONNX face recognition models are fully initialized
 *      (scrfd_500m.onnx, mobilefacenet.onnx, minifasnetv2.onnx)
 *
 * This ensures the app is 100% ready for offline use before the user sees it.
 * Fallback: always hides after 90 s so the app is never permanently stuck.
 */
function waitForAppReadyThenHideLoader(onnxReady: Promise<void>): void {
  const FALLBACK_MS = 90_000;
  // Quick loads just flash the generic "Loading" label — only escalate to the
  // specific stage messages once it's taking long enough to need reassurance.
  const DETAIL_DELAY_MS = 2_000;
  const fallbackTimer = setTimeout(hideLoader, FALLBACK_MS);

  let onnxDone = false;
  let swDone = false;
  let detailsEnabled = false;

  const updateMessage = () => {
    if (!detailsEnabled) return;
    if (!onnxDone) {
      setLoaderMessage('Preparando reconocimiento facial…');
    } else if (!swDone) {
      setLoaderMessage('Descargando recursos de la aplicación…');
    }
  };

  const detailTimer = setTimeout(() => {
    detailsEnabled = true;
    updateMessage();
  }, DETAIL_DELAY_MS);

  const onnxTracked = onnxReady.finally(() => { onnxDone = true; updateMessage(); });
  const swTracked = swReadyPromise().finally(() => { swDone = true; updateMessage(); });

  Promise.all([swTracked, onnxTracked])
    .finally(() => {
      clearTimeout(fallbackTimer);
      clearTimeout(detailTimer);
      hideLoader();
    });
}

initSyncWorker(queryClient);
void debugTelemetryService.load(); // Warm sync cache before any logTiming() call (OQ-3)
void initServiceWorkerBridge(); // Initialize MessageChannel for Background Sync

// Eagerly initialize ONNX models — capture promise so we can gate the loader on it
// (scrfd_500m.onnx + mobilefacenet.onnx + minifasnetv2.onnx must all be ready)
const onnxInitPromise = eagerInitializeOnnxModels();

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined;

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found');

const AppTree = (
  <StrictMode>
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <ToastProvider>
            <OfflineExitGuard />
            <ErrorBoundary fallback={<ErrorSentryPage />}>
              <AuthProvider>
                <DeviceEnrollProvider>
                  <DeviceEnrollBanner />
                  <ConsoleGate />
                  <DeviceLicenseProvider>
                    <DeviceLicenseModal />
                    <ProfileProvider>
                      <RouterProvider router={router} />
                      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
                    </ProfileProvider>
                  </DeviceLicenseProvider>
                </DeviceEnrollProvider>
              </AuthProvider>
            </ErrorBoundary>
          </ToastProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </I18nextProvider>
  </StrictMode>
);

/**
 * Google Maps loader that NEVER blocks children rendering.
 *
 * `@react-google-maps/api`'s `<LoadScript>` renders `loadingElement` INSTEAD of
 * children until the Google Maps script loads. When offline this means the
 * entire app tree stays unmounted — white screen.
 *
 * This version ALWAYS renders children immediately. Google Maps is loaded in
 * the background via a vanilla `<script>` tag. When it loads successfully we
 * call `proveOnline()` so `OfflineSafeRedirect` knows we have real
 * connectivity. When it fails, we stay at the default offline state — no
 * harm done.
 *
 * The `GoogleMapEmbed` component already guards against "Google Maps not yet
 * loaded" by checking `window.google?.maps`, so the background-load approach
 * is safe everywhere.
 */
function MapsLoader({ children }: { children: React.ReactNode }) {
  // Always render children — no loadingElement blocking
  return (
    <>
      {children}
      {/* Load Google Maps in background, never blocking the UI */}
      {GOOGLE_MAPS_API_KEY && <GoogleMapsBackgroundLoader />}
    </>
  );
}

/** Injects Google Maps script in a hidden element. No connectivity check — backend requests handle that. */
function GoogleMapsBackgroundLoader() {
  useEffect(() => {
    const checkIfAlreadyLoaded = () => {
      if (window.google?.maps) {
        return true;
      }
      return false;
    };

    if (checkIfAlreadyLoaded()) return;

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=marker&loading=async`;
    script.async = true;
    script.onload = () => {
      // Script loaded — Google Maps is now available. No connectivity check here;
      // backend requests (timelog POST, auth, sync) prove online status.
    };
    script.onerror = () => {
      // Script failed to load — already default offline, nothing to do
    };
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, []);

  return null;
}

const root = createRoot(rootEl);
root.render(
  <MapsLoader>
    {AppTree}
  </MapsLoader>,
);

// Hide loader only when SW cache AND ONNX models are both ready
waitForAppReadyThenHideLoader(onnxInitPromise);
